import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import {
  Badge,
  Box,
  Button,
  Group,
  NumberInput,
  Select,
  Stack,
  Switch,
  Table,
  Tabs,
  Text,
  TextInput,
} from '@mantine/core';
import { settingSchemaFor, type SettingKey, type Settings } from '@seo/contracts';
import { publicMessageOf, useTRPC } from '../lib/trpc';
import { useCan } from '../lib/session';
import classes from './EntityPage.module.css';

const LIMIT_LABELS: Record<string, string> = {
  bulkMaxDomainsPerWindow: 'Доменів на вікно масової операції',
  bulkWindowMinutes: 'Вікно масової операції, хв',
  trafficAtRiskThreshold: 'Поріг трафіку під ризиком, кліків',
  registrarRatePerMinute: 'Запитів до реєстратора / хв',
  gscRatePerMinute: 'Запитів до Search Console / хв',
  confirmationTtlMinutes: 'Термін підтвердження, хв',
  dailyTokenLimitPerKey: 'Денний ліміт токенів на ключ',
  maxParallelGenerations: 'Паралельних генерацій',
};

/**
 * Людські підписи для ключів `settings.*` (§12, розділи мокапа «Глобальні налаштування»).
 *
 * Ключ без запису тут падає назад на сам `SettingKey` — новий ключ у контрактах не ламає
 * екран, лише показує технічну назву, поки хтось не допише підпис.
 */
const SETTING_LABELS: Partial<Record<SettingKey, string>> = {
  'ai.default_provider': 'Основний провайдер',
  'ai.default_model': 'Модель за замовчуванням',
  'ai.temperature': 'Temperature',
  'ai.max_parallel_generations': 'Воркерів одночасно',
  'ai.daily_token_limit_per_key': 'Денний ліміт токенів на ключ',
  'ai.openrouter_key_id': 'Ключ OpenRouter (ID для білінгу)',
  'ai.openai_key_id': 'Ключ OpenAI (ID для білінгу)',
  'ai.anthropic_key_id': 'Ключ Anthropic (ID для білінгу)',
  'ai.max_retries': 'Ретраїв на елемент',
  'content.tone_of_voice': 'Tone of voice',
  'content.min_words_default': 'Мін. обʼєм сторінки, слів',
  'content.max_words_default': 'Макс. обʼєм сторінки, слів',
  'content.forbidden_phrases': 'Заборонені фрази',
  'content.uniqueness_threshold_pct': 'Поріг унікальності, %',
  'content.dictionary_coverage_min_pct': 'Мін. покриття словника, %',
  'seo.title_template': 'Патерн title',
  'seo.description_template': 'Патерн description',
  'seo.enable_faq_schema': 'FAQ schema',
  'seo.enable_breadcrumb_schema': 'Breadcrumb schema',
  'seo.max_html_kb': 'Макс. розмір HTML, KB',
  'tech.default_canonical': 'Канонічний у звʼязці EMD+дроп',
  'tech.default_hreflang_role': 'Hreflang-роль дропа',
  'tech.entity_fill_threshold_pct': 'Поріг заповненості сутності, %',
  'links.default_anchor_pct': 'Anchor % за замовчуванням',
  'links.min_donor_dr': 'Мін. DR донора',
  'links.max_spam_signals_pct': 'Макс. частка спам-сигналів у донора, %',
  'links.guest_post_min_words': 'Мін. обʼєм гестпоста, слів',
  'limits.bulk_max_domains_per_window': 'Доменів на вікно масової операції',
  'limits.bulk_window_minutes': 'Вікно масової операції, хв',
  'limits.bulk_batch_size': 'Розмір батчу масової операції',
  'limits.traffic_at_risk_threshold_clicks': 'Поріг трафіку під ризиком, кліків',
  'limits.registrar_rate_per_minute': 'Запитів до реєстратора / хв',
  'limits.gsc_rate_per_minute': 'Запитів до Search Console / хв',
  'limits.confirmation_ttl_minutes': 'Термін підтвердження, хв',
  'limits.emd_purchase_dual_confirm_threshold_minor':
    'Поріг двоособового підтвердження купівлі EMD, мінорні одиниці USD',
  'brand.default_currency': 'Валюта за замовчуванням',
  'brand.rounding_rule': 'Правило округлення',
  'monitor.ping_interval_minutes': 'Інтервал пінга, хв',
  'monitor.index_drop_incident_pct': 'Поріг інциденту падіння індексу, %',
  'monitor.uptime_timeout_ms': 'Таймаут перевірки uptime, мс',
  'notify.on_incident': 'Сповіщати про інциденти',
  'notify.on_abuse_critical': 'Сповіщати про критичну абузу',
  'notify.on_dlq_digest': 'Сповіщати про дайджест DLQ',
  'notify.on_deploy': 'Сповіщати про деплой',
  'import.max_file_mb': 'Макс. розмір файлу, MB',
  'import.batch_size': 'Розмір батчу імпорту',
  'import.keyword_min_volume': 'Мін. обʼєм кейворда / міс',
};

/**
 * Ключі, чиє нульове значення не забороняє запис (схема це дозволяє), а вимикає або
 * максимально жорсткить запобіжник, — і оператор має побачити наслідок, а не тільки число.
 *
 * `ai.daily_token_limit_per_key === 0` знімає денний ліміт токенів зовсім
 * (`assertTokenBudget` виходить одразу, `packages/core/src/services/limits.ts:114`).
 * `limits.traffic_at_risk_threshold_clicks === 0` діє у зворотний бік — з нуля будь-яка
 * операція проходить умову `trafficAtRisk >= threshold` і вимагає підтвердження
 * (`packages/core/src/services/bulk.ts:907`), тобто нуль тут не послаблює, а посилює.
 */
const ZERO_WARNING: Partial<Record<SettingKey, string>> = {
  'ai.daily_token_limit_per_key': '0 — ліміт вимкнено: токени не обмежені жодним числом.',
  'limits.traffic_at_risk_threshold_clicks':
    '0 — підтвердження двох людей вимагатиметься для будь-якої операції.',
};

const LEVEL_COLOR: Record<string, string> = {
  ok: 'mint',
  warn: 'amber',
  error: 'rose',
  off: 'slate',
};

interface SectionTab {
  id: string;
  label: string;
  note: string;
  prefix: string | null;
  alsoPrefixes?: string[] | undefined;
}

type FieldKind =
  | { type: 'boolean' }
  | { type: 'enum'; options: string[] }
  | { type: 'number' }
  | { type: 'array' }
  | { type: 'string' };

/**
 * Контрол обирається за реальною zod-схемою ключа, а не за типом значення, яке зараз лежить
 * у БД, — і не за здогадкою з назви ключа. Збережене «так» у числовому ключі — це відмова
 * сервера, якої оператор не спричиняв.
 */
function fieldKindOf(key: SettingKey): FieldKind {
  const schema = settingSchemaFor(key);
  const inner = schema instanceof z.ZodDefault ? schema.unwrap() : schema;
  if (inner instanceof z.ZodBoolean) return { type: 'boolean' };
  if (inner instanceof z.ZodEnum) return { type: 'enum', options: inner.options as string[] };
  if (inner instanceof z.ZodNumber) return { type: 'number' };
  if (inner instanceof z.ZodArray) return { type: 'array' };
  return { type: 'string' };
}

/**
 * `unknown` тут — не заглушка: значення приходить із `settings.all`, чий тип — повний
 * `Settings`, але доступ по динамічному `SettingKey` (`values[key]`) звужує його назад до
 * `unknown` для TS. Стрінгуємо лише відомі скалярні типи — інакше об'єкт мовчки ліг би в
 * рядок як `[object Object]`.
 */
function scalarText(value: unknown, empty = ''): string {
  if (value === null || value === undefined) return empty;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return empty;
}

function draftFrom(kind: FieldKind, value: unknown): string | number {
  if (kind.type === 'array') return Array.isArray(value) ? (value as string[]).join(', ') : '';
  if (kind.type === 'number') return typeof value === 'number' ? value : '';
  return scalarText(value);
}

function parseDraft(kind: FieldKind, draft: string | number): unknown {
  if (kind.type === 'number') return typeof draft === 'number' ? draft : Number(draft);
  if (kind.type === 'array') {
    return String(draft)
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean);
  }
  return String(draft);
}

function isDirty(kind: FieldKind, draft: string | number, value: unknown): boolean {
  if (kind.type === 'number') {
    if (draft === '') return false;
    const n = typeof draft === 'number' ? draft : Number(draft);
    return Number.isFinite(n) && n !== value;
  }
  if (kind.type === 'array') {
    const current = Array.isArray(value) ? (value as string[]).join(', ') : '';
    return String(draft) !== current;
  }
  return String(draft) !== scalarText(value);
}

function formatValue(kind: FieldKind, value: unknown): string {
  if (kind.type === 'array') {
    return Array.isArray(value) && value.length > 0 ? (value as string[]).join(', ') : '—';
  }
  if (kind.type === 'boolean') return value === true ? 'так' : 'ні';
  return scalarText(value, '—');
}

/** Власний префікс плюс ті, що переїхали сюди після зведення вкладок до девʼяти. */
function prefixesOf(section: SectionTab): string[] {
  return [...(section.prefix === null ? [] : [section.prefix]), ...(section.alsoPrefixes ?? [])];
}

function keysForSection(
  section: SectionTab,
  allKeys: SettingKey[],
  orphanKeys: SettingKey[],
): SettingKey[] {
  if (section.id === '_unassigned') return orphanKeys;
  const prefixes = prefixesOf(section);
  if (prefixes.length === 0) return [];
  return allKeys.filter((key) => prefixes.some((prefix) => key.startsWith(prefix)));
}

interface SettingRowProps {
  settingsKey: SettingKey;
  value: unknown;
  canWrite: boolean;
}

/**
 * Один рядок ключа: контрол обирається типом схеми, зберігає напряму (без проміжного
 * «режиму редагування» — 47 ключів на одному екрані не витримали б ще один клік на кожен).
 *
 * Після успіху показуємо значення, яке повернув сервер (`data.value`, уже спарсене
 * `settingSchemaFor`), а не те, що оператор надрукував, — інакше поле показувало б чернетку,
 * доки мутація ще летіла (§ «сигнали, які брешуть», п.2).
 */
function SettingRow({ settingsKey, value, canWrite }: SettingRowProps): React.JSX.Element {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const kind = fieldKindOf(settingsKey);
  const [draft, setDraft] = useState<string | number>(() => draftFrom(kind, value));

  const save = useMutation(
    trpc.settings.set.mutationOptions({
      onSuccess: (data) => {
        setDraft(draftFrom(kind, data.value));
        void queryClient.invalidateQueries({ queryKey: trpc.settings.pathKey() });
      },
    }),
  );

  const commit = (next: unknown): void => {
    save.mutate({ key: settingsKey, value: next });
  };

  const label = SETTING_LABELS[settingsKey] ?? settingsKey;
  const zeroWarning = kind.type === 'number' && value === 0 ? ZERO_WARNING[settingsKey] : undefined;

  return (
    <Box data-testid={`setting-${settingsKey}`} py={4}>
      <Group justify="space-between" wrap="nowrap" gap="sm" align="flex-start">
        <Text size="sm" style={{ flex: '0 0 260px' }}>
          {label}
        </Text>

        {!canWrite ? (
          <Text className={classes.mono} data-testid={`setting-${settingsKey}-value`}>
            {formatValue(kind, value)}
          </Text>
        ) : kind.type === 'boolean' ? (
          <Switch
            size="xs"
            checked={value === true}
            data-testid={`setting-${settingsKey}-input`}
            onChange={(event) => {
              const { checked } = event.currentTarget;
              commit(checked);
            }}
          />
        ) : kind.type === 'enum' ? (
          <Select
            size="xs"
            allowDeselect={false}
            data={kind.options.map((option) => ({ value: option, label: option }))}
            value={scalarText(value)}
            data-testid={`setting-${settingsKey}-input`}
            style={{ width: 220 }}
            onChange={(next) => {
              if (next !== null) commit(next);
            }}
          />
        ) : (
          <Group gap={6} wrap="nowrap">
            {kind.type === 'number' ? (
              <NumberInput
                size="xs"
                value={draft}
                data-testid={`setting-${settingsKey}-input`}
                style={{ width: 150 }}
                onChange={setDraft}
              />
            ) : (
              <TextInput
                size="xs"
                value={String(draft)}
                data-testid={`setting-${settingsKey}-input`}
                style={{ width: 240 }}
                onChange={(event) => {
                  const { value: raw } = event.currentTarget;
                  setDraft(raw);
                }}
              />
            )}
            <Button
              size="compact-xs"
              variant="default"
              loading={save.isPending}
              disabled={!isDirty(kind, draft, value)}
              data-testid={`setting-${settingsKey}-save`}
              onClick={() => {
                commit(parseDraft(kind, draft));
              }}
            >
              Зберегти
            </Button>
          </Group>
        )}
      </Group>

      {zeroWarning === undefined ? null : (
        <Text
          className={classes.warn}
          fz="xs"
          mt={2}
          data-testid={`setting-${settingsKey}-warning`}
        >
          {zeroWarning}
        </Text>
      )}

      {save.error == null ? null : (
        <Text
          className={classes.danger}
          fz="xs"
          mt={2}
          role="alert"
          data-testid={`setting-${settingsKey}-error`}
        >
          {publicMessageOf(save.error)}
        </Text>
      )}
    </Box>
  );
}

/**
 * Налаштування та запобіжники (§12), acceptance scenario 16.
 *
 * Верхні три панелі лишились без змін: вони показують ліміти так, як їх реально читає
 * сервіс (`limits.snapshot`, `limits.tokenBudget`, `limits.pendingConfirmations`) — власна
 * копія дефолтів на цьому екрані була б числом, якого ніщо не слухається.
 *
 * Розділи нижче — це те, чого не було: `settings.sections`, `settings.set` і
 * `settings.integrations` існували в API з першого дня без жодного викликача з UI. Екран
 * лише показував `settings.all` як пласку таблицю JSON — значення без способу їх змінити.
 *
 * Вкладки будуються з `settings.sections`, а не з копії переліку тут: коли розділи
 * «Моніторинг» і «Імпорт» приберуть із контрактів (залишивши самі ключі `monitor.*` /
 * `import.*`), список вкладок звузиться сам, без правки цього файлу. До того моменту ключі
 * без розділу (якщо такі з'являться) осідають у вкладці «Інше», а не зникають мовчки.
 */
export function SettingsPage(): React.JSX.Element {
  const trpc = useTRPC();
  const canWrite = useCan('system_admin', 'write');
  const [apiKeyId, setApiKeyId] = useState('openrouter');
  const [tab, setTab] = useState<string | null>(null);

  const limits = useQuery(trpc.limits.snapshot.queryOptions());
  const budget = useQuery(trpc.limits.tokenBudget.queryOptions({ apiKeyId }));
  const confirmations = useQuery(trpc.limits.pendingConfirmations.queryOptions());

  const sections = useQuery(trpc.settings.sections.queryOptions());
  const all = useQuery(trpc.settings.all.queryOptions());

  const activeTabId = tab ?? sections.data?.[0]?.id ?? null;

  // Лінива: агрегує кілька SQL-запитів (GSC, LLM, парсер, хостинг) — рахувати їх на кожному
  // рендері екрана, а не лише коли оператор відкрив саме цю вкладку, було б зайвою ціною.
  const integrations = useQuery({
    ...trpc.settings.integrations.queryOptions(),
    enabled: activeTabId === 'integrations',
  });

  const allKeys: SettingKey[] = all.data?.keys ?? [];
  const values: Partial<Settings> = all.data?.values ?? {};
  const knownSections = sections.data ?? [];
  const orphanKeys = allKeys.filter(
    (key) =>
      !knownSections.some((section) =>
        prefixesOf(section).some((prefix) => key.startsWith(prefix)),
      ),
  );
  const tabs: SectionTab[] = [
    ...knownSections,
    ...(orphanKeys.length > 0
      ? [{ id: '_unassigned', label: 'Інше', note: 'ключі без розділу в мокапі', prefix: null }]
      : []),
  ];

  return (
    <Stack gap="md">
      <Box className={classes.panel} data-testid="settings-limits">
        <Text className={classes.panelTitle} mb={8}>
          Ліміти й запобіжники
        </Text>
        <Table striped withRowBorders={false} verticalSpacing={4} fz="xs">
          <Table.Tbody>
            {Object.entries(limits.data ?? {}).map(([key, value]) => (
              <Table.Tr key={key} data-testid={`limit-${key}`}>
                <Table.Td>{LIMIT_LABELS[key] ?? key}</Table.Td>
                <Table.Td className={classes.mono}>{String(value)}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Box>

      <Box className={classes.panel} data-testid="settings-token-budget">
        <Group justify="space-between" mb={8}>
          <Text className={classes.panelTitle}>Денний бюджет токенів</Text>
          <TextInput
            size="xs"
            value={apiKeyId}
            onChange={(event) => {
              setApiKeyId(event.currentTarget.value);
            }}
            data-testid="budget-key"
            style={{ width: 180 }}
          />
        </Group>
        {budget.data === undefined ? (
          <Text className={classes.dim}>—</Text>
        ) : (
          <Group gap="lg">
            <Text className={classes.dim}>
              ліміт{' '}
              <span className={classes.mono} data-testid="budget-limit">
                {budget.data.limit}
              </span>
            </Text>
            <Text className={classes.dim}>
              витрачено{' '}
              <span className={classes.mono} data-testid="budget-used">
                {budget.data.usedToday}
              </span>
            </Text>
            <Text className={classes.dim}>
              лишилось{' '}
              <span className={classes.mono} data-testid="budget-remaining">
                {budget.data.remaining}
              </span>
            </Text>
            <Badge
              size="sm"
              color={budget.data.exhausted ? 'rose' : 'mint'}
              data-testid="budget-state"
            >
              {budget.data.exhausted ? 'вичерпано — генерація заблокована' : 'у межах'}
            </Badge>
          </Group>
        )}
      </Box>

      <Box className={classes.panel} data-testid="settings-confirmations">
        <Text className={classes.panelTitle} mb={8}>
          Операції, що чекають другого підтвердження
        </Text>
        {(confirmations.data ?? []).length === 0 ? (
          <Text className={classes.dim} data-testid="no-confirmations">
            Немає.
          </Text>
        ) : (
          <Stack gap={4}>
            {(confirmations.data ?? []).map((row) => (
              <Group key={row.id} gap={10} data-testid={`confirmation-${row.id}`}>
                <Text className={classes.mono} fz="xs">
                  {row.operationRef}
                </Text>
                <Badge size="xs" color={row.satisfied ? 'mint' : 'amber'}>
                  {row.confirmedBy.length} / 2
                </Badge>
                <Text className={classes.dim}>{row.reason ?? ''}</Text>
                {row.expired ? (
                  <Text className={classes.danger} data-testid={`confirmation-expired-${row.id}`}>
                    термін минув
                  </Text>
                ) : null}
              </Group>
            ))}
          </Stack>
        )}
      </Box>

      <Box className={classes.panel} data-testid="settings-editor">
        <Text className={classes.panelTitle} mb={8}>
          Налаштування за розділами
        </Text>

        <Tabs value={activeTabId} onChange={setTab} keepMounted={false} data-testid="settings-tabs">
          <Tabs.List>
            {tabs.map((section) => (
              <Tabs.Tab
                key={section.id}
                value={section.id}
                data-testid={`settings-tab-${section.id}`}
              >
                {section.label}
              </Tabs.Tab>
            ))}
          </Tabs.List>

          {tabs.map((section) => (
            <Tabs.Panel key={section.id} value={section.id} pt="sm">
              {section.note === '' ? null : (
                <Text className={classes.dim} mb={8}>
                  {section.note}
                </Text>
              )}

              {section.id === 'integrations' ? (
                <Stack gap={6} data-testid="settings-integrations">
                  {integrations.isPending ? (
                    <Text className={classes.dim}>завантаження…</Text>
                  ) : integrations.error != null ? (
                    <Text className={classes.danger} role="alert">
                      {publicMessageOf(integrations.error)}
                    </Text>
                  ) : (
                    (integrations.data ?? []).map((row) => (
                      <Group
                        key={row.key}
                        justify="space-between"
                        data-testid={`integration-${row.key}`}
                      >
                        <Box>
                          <Text size="sm">{row.label}</Text>
                          <Text className={classes.dim} fz="xs">
                            {row.detail}
                          </Text>
                        </Box>
                        <Badge
                          size="sm"
                          color={LEVEL_COLOR[row.level] ?? 'slate'}
                          data-testid={`integration-${row.key}-level`}
                        >
                          {row.level}
                        </Badge>
                      </Group>
                    ))
                  )}
                </Stack>
              ) : (
                <Stack gap={2} data-testid={`settings-section-${section.id}`}>
                  {keysForSection(section, allKeys, orphanKeys).map((key) => (
                    <SettingRow
                      key={key}
                      settingsKey={key}
                      value={values[key]}
                      canWrite={canWrite}
                    />
                  ))}
                </Stack>
              )}
            </Tabs.Panel>
          ))}
        </Tabs>
      </Box>
    </Stack>
  );
}
